import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, LifeBuoy, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { TicketStatusBadge, TicketPriorityBadge } from '../components/support/StatusBadge';
import { NewTicketDialog } from '../components/support/NewTicketDialog';
import { CustomerTicketDetail } from '../components/support/CustomerTicketDetail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'open', label: 'Open' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'archived', label: 'Archived' },
];

export default function SupportTicketsPage() {
  const { user, token, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login?redirect=/support');
    }
  }, [loading, isAuthenticated, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${BACKEND_URL}/api/portal/tickets`, { headers, params });
      setTickets(res.data);
    } catch (error) {
      // silent - transient network issue, user can retry via tab click
    } finally {
      setFetching(false);
    }
  }, [token, statusFilter]);

  useEffect(() => { if (isAuthenticated) fetchTickets(); }, [isAuthenticated, fetchTickets]);

  const openTicket = async (id) => {
    setActiveTicketId(id);
    const res = await axios.get(`${BACKEND_URL}/api/portal/tickets/${id}`, { headers });
    setActiveTicket(res.data);
  };

  const refreshActiveTicket = async () => {
    if (!activeTicketId) return;
    const res = await axios.get(`${BACKEND_URL}/api/portal/tickets/${activeTicketId}`, { headers });
    setActiveTicket(res.data);
    fetchTickets();
  };

  const filteredTickets = tickets.filter((t) =>
    !search.trim() ||
    t.subject?.toLowerCase().includes(search.toLowerCase()) ||
    String(t.ticket_number).includes(search)
  );

  if (loading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 pt-28 pb-8" data-testid="support-tickets-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-[#6e2ea8]" />
            <h1 className="text-2xl font-bold text-slate-800">Support</h1>
          </div>
          <Button className="bg-[#6e2ea8] hover:bg-[#5a2690]" onClick={() => setShowNewTicket(true)} data-testid="open-new-ticket-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Ticket
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex gap-1 bg-white border rounded-lg p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === tab.id ? 'bg-[#6e2ea8] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid={`support-filter-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search tickets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="support-search-input" />
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {fetching ? (
              <div className="py-16 text-center text-slate-400 text-sm">Loading tickets...</div>
            ) : filteredTickets.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-tickets-empty-state">
                <LifeBuoy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">No tickets yet. Need help? Open a new ticket above.</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredTickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t.id)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`ticket-row-${t.ticket_number}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">#{t.ticket_number} &mdash; {t.subject}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Updated {new Date(t.updated_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <TicketPriorityBadge priority={t.priority} />
                      <TicketStatusBadge status={t.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />

      <NewTicketDialog
        open={showNewTicket}
        onClose={() => setShowNewTicket(false)}
        token={token}
        onCreated={(ticket) => { setShowNewTicket(false); fetchTickets(); openTicket(ticket.id); }}
      />

      <CustomerTicketDetail
        ticket={activeTicket}
        token={token}
        onClose={() => { setActiveTicketId(null); setActiveTicket(null); }}
        onUpdated={refreshActiveTicket}
      />
    </div>
  );
}
